'use client';
import React, { useState, useEffect } from 'react';
import { Download, X, Smartphone, Monitor } from 'lucide-react';
import { usePWAInstall } from '@/hooks/usePWAInstall';
import { getStoredLanguage } from '@/lib/i18n';

interface PWAInstallBannerProps {
  appName?: string;
  appNameAr?: string;
  themeColor?: string;
  compact?: boolean; // for topbar button mode
}

export default function PWAInstallBanner({
  appName = 'Nexora ERP',
  appNameAr = 'نيكسورا ERP',
  themeColor = '#0F5C5C',
  compact = false,
}: PWAInstallBannerProps) {
  const { canInstall, isInstalled, isIOS, promptInstall } = usePWAInstall();
  const [dismissed, setDismissed] = useState(false);
  const [showIOSGuide, setShowIOSGuide] = useState(false);
  const [lang, setLang] = useState<'ar' | 'en'>('ar');

  useEffect(() => {
    setLang(getStoredLanguage());
    const handler = () => setLang(getStoredLanguage());
    window.addEventListener('nexora_lang_change', handler);

    // Check if user already dismissed
    const wasDismissed = localStorage.getItem('pwa_banner_dismissed');
    if (wasDismissed) setDismissed(true);

    return () => window.removeEventListener('nexora_lang_change', handler);
  }, []);

  const handleDismiss = () => {
    setDismissed(true);
    localStorage.setItem('pwa_banner_dismissed', '1');
  };

  const handleInstall = async () => {
    if (isIOS) {
      setShowIOSGuide(true);
    } else {
      await promptInstall();
    }
  };

  // Don't show if installed, dismissed, or nothing to show
  if (isInstalled || dismissed) return null;
  if (!canInstall && !isIOS) return null;

  const isAr = lang === 'ar';

  // Compact mode: just a button for Topbar
  if (compact) {
    return (
      <button
        onClick={handleInstall}
        className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-semibold transition-all hover:opacity-90 active:scale-95"
        style={{ background: `${themeColor}15`, color: themeColor, border: `1px solid ${themeColor}30` }}
        title={isAr ? 'تثبيت التطبيق' : 'Install App'}
      >
        <Download size={12} />
        <span className="hidden sm:inline">{isAr ? 'تثبيت' : 'Install'}</span>
      </button>
    );
  }

  return (
    <>
      {/* Install Banner */}
      <div
        className="fixed bottom-4 left-4 right-4 sm:left-auto sm:right-4 sm:w-80 z-[9999] rounded-2xl shadow-2xl border overflow-hidden"
        style={{ background: 'var(--card)', borderColor: `${themeColor}30` }}
        dir={isAr ? 'rtl' : 'ltr'}
      >
        {/* Color accent top bar */}
        <div className="h-1" style={{ background: `linear-gradient(90deg, ${themeColor}, #C9A84C)` }} />

        <div className="p-4">
          <div className="flex items-start gap-3">
            {/* App icon */}
            <div
              className="w-12 h-12 rounded-xl flex items-center justify-center shrink-0 text-white font-black text-lg"
              style={{ background: `linear-gradient(135deg, ${themeColor}, #C9A84C)` }}
            >
              N
            </div>

            <div className="flex-1 min-w-0">
              <p className="text-sm font-black text-foreground leading-tight">
                {isAr ? `ثبّت ${appNameAr}` : `Install ${appName}`}
              </p>
              <p className="text-[11px] text-muted-foreground mt-0.5 leading-snug">
                {isAr
                  ? 'أضف التطبيق إلى شاشتك الرئيسية للوصول السريع' :'Add to your home screen for quick access'}
              </p>
            </div>

            <button
              onClick={handleDismiss}
              className="p-1 rounded-lg hover:bg-muted text-muted-foreground hover:text-foreground transition-colors shrink-0"
            >
              <X size={14} />
            </button>
          </div>

          <div className="flex items-center gap-2 mt-3">
            <button
              onClick={handleInstall}
              className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-xl text-xs font-bold text-white transition-all hover:opacity-90 active:scale-95"
              style={{ background: `linear-gradient(135deg, ${themeColor}, #0A3D3D)` }}
            >
              <Download size={12} />
              {isAr ? 'تثبيت الآن' : 'Install Now'}
            </button>
            <button
              onClick={handleDismiss}
              className="px-3 py-2 rounded-xl text-xs font-semibold text-muted-foreground hover:bg-muted transition-colors"
            >
              {isAr ? 'لاحقاً' : 'Later'}
            </button>
          </div>

          {/* Device icons */}
          <div className="flex items-center gap-1.5 mt-2.5 justify-center">
            <Smartphone size={11} className="text-muted-foreground" />
            <Monitor size={11} className="text-muted-foreground" />
            <span className="text-[10px] text-muted-foreground">
              {isAr ? 'يعمل على جميع الأجهزة' : 'Works on all devices'}
            </span>
          </div>
        </div>
      </div>

      {/* iOS Guide Modal */}
      {showIOSGuide && (
        <div
          className="fixed inset-0 z-[10000] flex items-end justify-center bg-black/50 backdrop-blur-sm p-4"
          onClick={() => setShowIOSGuide(false)}
          dir={isAr ? 'rtl' : 'ltr'}
        >
          <div
            className="w-full max-w-sm rounded-2xl p-5 shadow-2xl"
            style={{ background: 'var(--card)' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-black text-foreground">
                {isAr ? 'تثبيت على iPhone / iPad' : 'Install on iPhone / iPad'}
              </h3>
              <button onClick={() => setShowIOSGuide(false)} className="p-1 rounded-lg hover:bg-muted">
                <X size={14} className="text-muted-foreground" />
              </button>
            </div>
            <ol className="space-y-3">
              {(isAr ? [
                'اضغط على زر المشاركة ⬆️ في شريط Safari السفلي',
                'مرّر للأسفل واختر "إضافة إلى الشاشة الرئيسية"',
                'اضغط "إضافة" في الزاوية العلوية',
              ] : [
                'Tap the Share button ⬆️ in Safari\'s bottom bar',
                'Scroll down and tap "Add to Home Screen"',
                'Tap "Add" in the top corner',
              ]).map((step, i) => (
                <li key={i} className="flex items-start gap-3">
                  <span
                    className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-black text-white shrink-0"
                    style={{ background: themeColor }}
                  >
                    {i + 1}
                  </span>
                  <span className="text-sm text-foreground leading-snug">{step}</span>
                </li>
              ))}
            </ol>
            <button
              onClick={() => setShowIOSGuide(false)}
              className="w-full mt-4 py-2.5 rounded-xl text-sm font-bold text-white"
              style={{ background: themeColor }}
            >
              {isAr ? 'فهمت' : 'Got it'}
            </button>
          </div>
        </div>
      )}
    </>
  );
}
